import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import { P, H4 } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';
import { T_EmptyState } from '@/ts/types/T_Profile';

import NoDataWrapper from '../NoDataWrapper';

type T_Variant = 'predictions' | 'pools' | 'referrals';

interface I_SectionRightBlockProps {
  variant: T_Variant;
  symbol: string;
  isEmptyState: boolean;
  emptyState: T_EmptyState | null;
  predictionsHistoryList: React.ReactNode | null;
  poolsList: React.ReactNode | null;
  referralsList: React.ReactNode | null;
}

export function SectionRightBlock({
  variant,
  symbol,
  isEmptyState,
  emptyState,
  predictionsHistoryList,
  poolsList,
  referralsList
}: I_SectionRightBlockProps) {
  const t = useTranslations();
  const isReferrals = variant === 'referrals';
  const isPredictions = variant === 'predictions';

  const rightBlock = useMemo(() => {
    switch (variant) {
      case 'predictions':
        return predictionsHistoryList;
      case 'pools':
        return poolsList;
      case 'referrals':
        return referralsList;
      default:
        return null;
    }
  }, [variant, predictionsHistoryList, poolsList, referralsList]);

  return (
    <div className="w-full md:w-[50%]">
      {isEmptyState ? (
        <NoDataWrapper
          emptyState={{
            title: t(emptyState?.titleKey || ''),
            text: t(emptyState?.textKey || ''),
            actionText: emptyState?.actionTextKey
              ? t(emptyState.actionTextKey)
              : ''
          }}
          variant={variant}
        />
      ) : (
        <>
          {isReferrals ? (
            <P className="text-center  mb-5">
              {t('profile.sections.referrals.content.title')}
            </P>
          ) : (
            <H4 className="mb-5">
              {isPredictions
                ? t('profile.sections.predictions.content.title')
                : variant === 'pools'
                  ? t('profile.sections.pools.content.title')
                  : ''}
            </H4>
          )}
          <div className="mb-5">{rightBlock}</div>
          <Button className="w-full h-10" asChild>
            <Link
              href={
                variant === 'pools' && symbol
                  ? `/profile/pools/${symbol.toLowerCase()}`
                  : `/profile/${variant}`
              }
            >
              {t('profile.actions.viewAll')}
            </Link>
          </Button>
        </>
      )}
    </div>
  );
}
